/**
 * SIM Mega Menu
 * Handles: L2 hover switching, L4 toggle, hover intent, keyboard nav, accessibility
 */

(function () {
  'use strict';

  const HOVER_INTENT_DELAY = 80; // ms before panel opens — prevents flashing
  const HOVER_LEAVE_DELAY = 200; // ms before panel closes — allows moving to panel

  class SimMegaMenu {
    constructor(nav) {
      this.nav = nav;
      this.items = Array.from(nav.querySelectorAll('.sim-mega-nav__item--has-mega'));
      this.openItem = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.boundUpdatePosition = () => this.updateOpenPanelPosition();

      this.init();
    }

    getPanelTop() {
      return this.nav.getBoundingClientRect().bottom;
    }

    init() {
      this.items.forEach(item => {
        const l1Link = item.querySelector('.sim-mega-nav__l1-link');
        const panel = item.querySelector('.sim-mega-panel');

        if (!panel) return;

        // Show panel on L1 hover with intent delay
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => this.openPanel(item), HOVER_INTENT_DELAY);
        });

        // Hide panel on mouse leave with delay
        item.addEventListener('mouseleave', () => {
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.closePanel(item), HOVER_LEAVE_DELAY);
        });

        // Keep panel open when mouse moves into it
        panel.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
        });

        panel.addEventListener('mouseleave', () => {
          this.closeTimer = setTimeout(() => this.closePanel(item), HOVER_LEAVE_DELAY);
        });

        // Keyboard: open on Enter/Space on L1 link
        l1Link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (item.classList.contains('sim-mega-nav__item--open')) {
              this.closePanel(item);
            } else {
              this.openPanel(item);
            }
          }
          if (e.key === 'Escape') {
            this.closePanel(item);
            l1Link.focus();
          }
        });

        // Close on Escape inside panel
        panel.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.closePanel(item);
            l1Link.focus();
          }
        });

        // Init L2 switching for this panel
        this.initL2Switching(panel);

        // Init L4 toggles for this panel
        this.initL4Toggles(panel);
      });

      // Close all panels when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.nav.contains(e.target)) {
          this.closeAllPanels();
        }
      });

      // Close on focus outside
      document.addEventListener('focusin', (e) => {
        if (!this.nav.contains(e.target)) {
          this.closeAllPanels();
        }
      });

      // Keep panel position correct as the sticky header moves/resizes
      window.addEventListener('scroll', this.boundUpdatePosition, { passive: true });
      window.addEventListener('resize', this.boundUpdatePosition, { passive: true });

      // Init mobile drawer
      this.initMobileNav();
    }

    updateOpenPanelPosition() {
      if (!this.openItem) return;
      const panel = this.openItem.querySelector('.sim-mega-panel');
      if (panel) panel.style.top = this.getPanelTop() + 'px';
    }

    openPanel(item) {
      // Close any other open panel first
      if (this.openItem && this.openItem !== item) {
        this.closePanel(this.openItem);
      }

      const l1Link = item.querySelector('.sim-mega-nav__l1-link');
      const panel = item.querySelector('.sim-mega-panel');

      // Position panel just below the nav bar
      if (panel) panel.style.top = this.getPanelTop() + 'px';

      // Always reset L2/L3 to first item on open — handles case where the
      // panel stayed open (close timer was cancelled) and stale state remains
      if (panel) {
        const l2Items = panel.querySelectorAll('.sim-mega-panel__l2-item');
        const l3Panels = panel.querySelectorAll('.sim-mega-panel__l3-panel');
        l2Items.forEach(i => i.classList.remove('is-active'));
        l3Panels.forEach(p => p.classList.remove('is-visible'));
        if (l2Items[0]) l2Items[0].classList.add('is-active');
        if (l3Panels[0]) l3Panels[0].classList.add('is-visible');
      }

      item.classList.add('sim-mega-nav__item--open');
      l1Link.setAttribute('aria-expanded', 'true');
      this.openItem = item;
    }

    closePanel(item) {
      const l1Link = item.querySelector('.sim-mega-nav__l1-link');
      item.classList.remove('sim-mega-nav__item--open');
      l1Link.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;

      // Collapse all L4 toggles
      const panel = item.querySelector('.sim-mega-panel');
      if (panel) {
        panel.querySelectorAll('.sim-mega-panel__l4-list.is-open').forEach(list => {
          list.classList.remove('is-open');
          list.setAttribute('aria-hidden', 'true');
        });
        panel.querySelectorAll('[data-l4-toggle][aria-expanded="true"]').forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false');
        });
      }
    }

    closeAllPanels() {
      this.items.forEach(item => this.closePanel(item));
    }

    initL2Switching(panel) {
      const l2Items = Array.from(panel.querySelectorAll('.sim-mega-panel__l2-item'));
      const l3Panels = Array.from(panel.querySelectorAll('.sim-mega-panel__l3-panel'));

      if (l2Items.length === 0) return;

      // Show first panel by default
      if (l3Panels.length > 0) {
        l3Panels[0].classList.add('is-visible');
      }

      l2Items.forEach((l2Item) => {
        const l2Link = l2Item.querySelector('.sim-mega-panel__l2-link');
        if (!l2Link) return;

        const activate = () => {
          // Deactivate all
          l2Items.forEach(i => i.classList.remove('is-active'));
          l3Panels.forEach(p => p.classList.remove('is-visible'));

          // Activate this one — match by data attribute value
          l2Item.classList.add('is-active');
          const index = l2Item.getAttribute('data-l2-index');
          const targetPanel = panel.querySelector(`.sim-mega-panel__l3-panel[data-l3-panel="${index}"]`);
          if (targetPanel) targetPanel.classList.add('is-visible');
        };

        l2Item.addEventListener('mouseenter', activate);
        l2Link.addEventListener('focus', activate);
      });
    }

    initMobileNav() {
      const wrapper = this.nav.closest('.sim-mega-menu-wrapper');
      if (!wrapper) return;

      const toggle = wrapper.querySelector('.sim-mobile-toggle');
      const drawer = wrapper.querySelector('.sim-mobile-drawer');
      const overlay = wrapper.querySelector('.sim-mobile-overlay');
      const closeBtn = drawer && drawer.querySelector('.sim-mobile-drawer__close');

      if (!toggle || !drawer) return;

      const openDrawer = () => {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        overlay && overlay.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
      };

      const closeDrawer = () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        overlay && overlay.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      };

      toggle.addEventListener('click', () => {
        drawer.classList.contains('is-open') ? closeDrawer() : openDrawer();
      });

      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
      if (overlay) overlay.addEventListener('click', closeDrawer);

      // Accordion toggles — only the caret button expands/collapses child lists
      drawer.querySelectorAll('.sim-mobile-nav__toggle').forEach(btn => {
        btn.addEventListener('click', () => {
          const row = btn.parentElement;
          const list = row.nextElementSibling;
          if (!list) return;

          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          list.classList.toggle('is-open', !isOpen);
        });
      });
    }

    initL4Toggles(panel) {
      const toggles = Array.from(panel.querySelectorAll('[data-l4-toggle]'));

      toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const l3Item = toggle.closest('.sim-mega-panel__l3-item');
          const l4List = l3Item.querySelector('.sim-mega-panel__l4-list');
          if (!l4List) return;

          const isOpen = l4List.classList.contains('is-open');

          if (isOpen) {
            l4List.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
            l4List.setAttribute('aria-hidden', 'true');
          } else {
            l4List.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            l4List.setAttribute('aria-hidden', 'false');
          }
        });
      });
    }
  }

  // Init on DOM ready
  function init() {
    const navs = document.querySelectorAll('.sim-mega-nav');
    navs.forEach(nav => new SimMegaMenu(nav));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
